/**
 * 首页商品列表组件
 * 融合PC端ProListSwiper和移动端RegularList2025的功能
 * 支持响应式设计，根据设备类型使用不同的布局和交互方式
 */

'use client';

import { ListProduct } from '@/api/types/public';
import HomePageTitle from '@/app/(home-page)/components/HomePageTitle';
import { useFetchDynamicProducts } from '@/app/(home-page)/hooks/useFetchDynamicProducts';
import ProductCard from '@/components/Product/ProductCard';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import Link from 'next/link';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
// @ts-ignore: Icon library missing type definitions
import { RightArrowOutlined, ToRightArrow } from '@zeelool/icon';
import './product-list-navigation.css';
import SkeletonProductCard from '@/components/Product/SkeletonProductCard';
import { useInView } from 'react-intersection-observer';

// 导入统一的类型定义
import { ProductListProps } from '@/app/(home-page)/types';
import { useProductListTabState } from '../../hooks/useProductListTabState';
import { getSDEventParams } from '@/app/(home-page)/utils/tracking';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { setBlockFlags } from '@/utils/feature-value';
import { setSession } from '@/utils/tools';
import { useRouter } from 'next/navigation';
import { handlePageFlagsInfo } from '../../hooks/useHomeSensorsTracking';

export default function ProductList({
  indexData,
  moduleIndex,
  isSale = false,
  isSaleSecond = false,
  onTrack,
  onExpTrack,
  onValidExpTrack,
  homeListData = [],
}: ProductListProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const router = useRouter();

  const productGroupPath = indexData.product_group?.path || indexData.product_group?.id;

  // 为每个组件实例生成唯一ID
  const uniqueId = useMemo(
    () => `product-list-${String(indexData.id || moduleIndex).replace(/[^a-zA-Z0-9-]/g, '-')}`,
    [indexData.id, moduleIndex],
  );

  // 状态管理 - 简化状态，只保留必要的状态，并使用 SWR 缓存
  const { clickTab, setClickTab } = useProductListTabState(uniqueId);

  // 添加已曝光商品ID的记录Set
  const exposedProductIds = useRef(new Set<string>()).current;
  const validExposedProductIds = useRef(new Set<string>()).current;
  const swiperRef = useRef<any>(null);

  const isPC = deviceType === EPlatform.WEB;

  // 计算按钮样式
  const btnStyle1 = useMemo(() => {
    if (indexData.btn_display === 2) {
      return {
        backgroundColor: indexData.tab_bg_color || 'transparent',
        color: indexData.tab_word_color || '#333',
        borderColor: indexData.tab_word_color || '#333',
      };
    }
    return {};
  }, [indexData]);

  const btnStyle2 = useMemo(() => {
    if (indexData.btn_display === 2) {
      return {
        backgroundColor: indexData.choosed_bg_color || '#333',
        color: indexData.choosed_word_color || '#fff',
      };
    }
    return {};
  }, [indexData]);

  const { ref: inViewRef, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  const { dynamicProductsData, isLoading, hasAttemptedFetch } = useFetchDynamicProducts<any>({
    componentId: indexData?.component_id,
    componentBlockId: indexData?.id,
    currencyName: currency?.name,
    inView,
    hasProducts: false, // ProductList 总是需要获取动态数据
  });

  // 获取当前产品列表
  const products = useMemo(() => {
    const dataSource = { ...indexData, ...dynamicProductsData };

    // 处理多tab情况
    if (dataSource.btn_display === 2) {
      return clickTab === '1' ? dataSource.products || [] : dataSource.products_2 || [];
    }
    return dataSource.products || [];
  }, [indexData, clickTab, dynamicProductsData]);

  const hasProducts = products && products.length > 0;

  // PC端Swiper配置 - 简化配置
  const pcSwiperOptions = useMemo(
    () => ({
      modules: [Navigation],
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      slidesPerView: 'auto' as const,
      navigation: {
        nextEl: `.${uniqueId}-next`,
        prevEl: `.${uniqueId}-prev`,
      },
    }),
    [uniqueId],
  );

  // 移动端Swiper配置 - 参考Vue RegularList2025
  const mobileSwiperOptions = useMemo(
    () => ({
      modules: [Navigation],
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      slidesPerView: 2.2,
      spaceBetween: 5,
      slidesOffsetBefore: 14,
      slidesOffsetAfter: 14,
    }),
    [],
  );

  const swiperOptions = isPC ? pcSwiperOptions : mobileSwiperOptions;

  const setPageFlagsInfo = function () {
    const sdParams = {
      module_id: String(indexData.blocks_advances_id), // 已审核
      module_title: indexData.layout_title,
      module_index: moduleIndex,
      module_name: indexData.component_id, // 已审核
      mkt_jump_type: '1',
      group_id: indexData.product_group_id,
    };
    const pageFlagsInfo = handlePageFlagsInfo(sdParams);
    if (pageFlagsInfo) {
      setSession('zl_home_page_flags_info', pageFlagsInfo);
    }
  };

  // Tab切换处理
  const handleTabClick = (tab: string) => {
    if (tab === '3') {
      // 跳转到更多页面
      setPageFlagsInfo();
      setSession('zl_goodsListEnterPageScene', '首页坑位');
      router.push(`/goods-list/${productGroupPath}`);
    } else {
      setClickTab(tab);
      if (swiperRef.current?.swiper) {
        swiperRef.current.swiper.slideTo(0, 0);
      }
    }
  };

  // 埋点处理
  const handleSDHomeMktClick = useCallback(
    (item: ListProduct, index: number, clickIndex: number) => {
      try {
        if (onTrack) {
          const eventParams = getSDEventParams(
            item,
            indexData,
            moduleIndex,
            index,
            clickIndex,
            clickTab,
            homeListData,
            currency,
            userInfo,
          );

          // 设置block_flags - 使用全局版本
          setBlockFlags({
            block_flags: eventParams.block_flags,
            block_flags_ext: eventParams.block_flags_ext,
            fine_ranking_type: eventParams.fine_ranking_type,
            recall_group_type: eventParams.recall_group_type,
          });

          onTrack(eventParams);
        }
      } catch (e) {
        logger.error('埋点错误:', e);
      }
    },
    [moduleIndex, onTrack, indexData, clickTab, homeListData, currency, userInfo],
  );

  // 通用的曝光处理逻辑
  const handleExposure = useCallback(
    (
      item: ListProduct,
      index: number,
      clickIndex: number,
      exposedSet: Set<string>,
      callback?: (eventParams: any) => void,
    ) => {
      try {
        // 检查商品是否已经曝光过
        const productKey = `${item.id}-${moduleIndex}`;
        if (exposedSet.has(productKey)) {
          return; // 如果已经曝光过，直接返回，不再触发曝光事件
        }

        if (callback) {
          const eventParams = getSDEventParams(
            item,
            indexData,
            moduleIndex,
            index,
            clickIndex,
            clickTab,
            homeListData,
            currency,
            userInfo,
          );

          callback(eventParams);

          // 标记该商品已曝光
          exposedSet.add(productKey);
        }
      } catch (e) {
        logger.error('埋点错误:', e);
      }
    },
    [clickTab, moduleIndex, indexData, homeListData, currency, userInfo],
  );

  const handleExposeFunc = useCallback(
    (item: ListProduct, index: number, clickIndex: number) => {
      handleExposure(item, index, clickIndex, exposedProductIds, onExpTrack);
    },
    [handleExposure, exposedProductIds, onExpTrack],
  );

  const handleValidExposure = useCallback(
    (item: ListProduct, index: number, clickIndex: number) => {
      handleExposure(item, index, clickIndex, validExposedProductIds, onValidExpTrack);
    },
    [handleExposure, validExposedProductIds, onValidExpTrack],
  );

  // 检查是否显示标题
  const shouldShowTitle = indexData?.layout_title && indexData?.layout_title_status === 1;

  // 如果请求过数据并且数据为空，则不渲染该组件
  if (hasAttemptedFetch && !hasProducts) {
    return null;
  }

  return (
    <div
      ref={inViewRef}
      className={`
        pro-picture w-full
        ${isSale ? 'is-sale' : ''}
        ${isSaleSecond ? 'is-sale-second' : ''}
        my-[40px] sm:my-[80px]
      `}
    >
      {/* 标题和按钮区域 */}
      <div
        className={`
          title-box flex items-center
          ${(() => {
            if (isPC) {
              return shouldShowTitle ? 'justify-between' : 'justify-end';
            }
            return shouldShowTitle ? 'justify-center' : 'justify-end';
          })()}
          px-[14px] sm:px-[24px] lg:px-[48px]
        `}
      >
        {/* 标题部分 - 只有在应该显示时才渲染 */}
        {shouldShowTitle && (
          <HomePageTitle
            indexData={indexData}
            variant={isPC ? 'inline' : 'default'}
            titleTag="h3"
            subtitleTag="h5"
            hasButton={indexData.btn_display > 0}
          />
        )}

        {/* PC端按钮区域 */}
        {isPC && indexData.btn_display === 2 && (
          <div className="tab-box flex">
            <button
              className="tab-btn flex items-center justify-center min-w-[113px] lg:min-w-[142px] h-[36px] lg:h-[50px] text-[12px] lg:text-[16px] border border-transparent rounded-[50px] px-[12px] lg:px-[15px] ml-[6px] lg:ml-[8px] cursor-pointer transition-all duration-200 hover:opacity-90"
              style={clickTab === '1' ? btnStyle2 : btnStyle1}
              onClick={() => handleTabClick('1')}
            >
              Eyeglasses
            </button>
            <button
              className="tab-btn flex items-center justify-center min-w-[113px] lg:min-w-[142px] h-[36px] lg:h-[50px] text-[12px] lg:text-[16px] border border-transparent rounded-[50px] px-[12px] lg:px-[15px] ml-[6px] lg:ml-[8px] cursor-pointer transition-all duration-200 hover:opacity-90"
              style={clickTab === '2' ? btnStyle2 : btnStyle1}
              onClick={() => handleTabClick('2')}
            >
              Sunglasses
            </button>
            <Link
              href={`/goods-list/${productGroupPath}`}
              className="tab-btn flex items-center justify-center min-w-[113px] lg:min-w-[142px] h-[36px] lg:h-[50px] text-[12px] lg:text-[16px] border border-transparent rounded-[50px] px-[12px] lg:px-[15px] ml-[6px] lg:ml-[8px] cursor-pointer transition-all duration-200 hover:opacity-90"
              style={btnStyle1}
              onClick={() => {
                setSession('zl_goodsListEnterPageScene', '首页坑位');
                handleTabClick('3');
              }}
            >
              <span>View More</span>
              <RightArrowOutlined className="ml-[5px] text-[12px]" />
            </Link>
          </div>
        )}

        {/* PC端单个按钮 */}
        {isPC && indexData.btn_display === 1 && (
          <Link
            href={`/goods-list/${productGroupPath}`}
            className="view-more min-w-[7.3958333333vw]  flex items-center justify-center px-[12px] lg:px-[15px] h-[36px] lg:h-[50px] text-[12px] lg:text-[16px] border border-transparent rounded-[50px] cursor-pointer transition-all duration-200 hover:opacity-90"
            style={{
              backgroundColor: indexData.btn_bg_color || '#333',
              color: indexData.btn_word_color || '#fff',
              borderColor: indexData.btn_word_color || '#333',
            }}
            onClick={() => {
              setPageFlagsInfo();
              setSession('zl_goodsListEnterPageScene', '首页坑位');
            }}
          >
            <span>{indexData.btn_text || 'View More'}</span>
            <RightArrowOutlined className="ml-[5px] text-[12px]" />
          </Link>
        )}
      </div>

      {/* 移动端Tab按钮 */}
      {!isPC && indexData.btn_display === 2 && (
        <div className="tab-box flex justify-center items-center mt-[10px]">
          <button
            className="tab-btn flex items-center justify-center w-[98px] h-[28px] text-[12px] border rounded-[20px] mx-[3px] cursor-pointer transition-all duration-200"
            style={clickTab === '1' ? btnStyle2 : btnStyle1}
            onClick={() => handleTabClick('1')}
          >
            Eyeglasses
          </button>
          <button
            className="tab-btn flex items-center justify-center w-[98px] h-[28px] text-[12px] border rounded-[20px] mx-[3px] cursor-pointer transition-all duration-200"
            style={clickTab === '2' ? btnStyle2 : btnStyle1}
            onClick={() => handleTabClick('2')}
          >
            Sunglasses
          </button>
          <button
            className="tab-btn flex items-center justify-center w-[98px] h-[28px] text-[12px] border rounded-[20px] mx-[3px] cursor-pointer transition-all duration-200"
            style={btnStyle1}
            onClick={() => handleTabClick('3')}
          >
            <span>View More 2</span>
            <RightArrowOutlined className="ml-[5px] text-[12px]" />
          </button>
        </div>
      )}

      {/* 产品轮播区域 - 参考 SwiperNavigation 的容器类名 */}
      <div
        className={`
          product-swiper-container relative
          mt-[20px] sm:mt-[32px]
        `}
      >
        <Swiper
          ref={swiperRef}
          {...swiperOptions}
          className={`
            sm:!px-[24px] lg:!px-[48px] !pb-[2px]
          `}
        >
          {hasProducts
            ? products.map((item: any, index: number) => (
                <SwiperSlide
                  key={item.id}
                  className={`
                  swiper-item relative
                  ${isPC ? '!w-[23%] mr-[16px] last:!mr-0 !min-w-[328px] 2xl:!min-w-[auto]' : ''}
                `}
                >
                  <ProductCard
                    product={item}
                    index={index}
                    showCorrelation
                    showReviewStar
                    showTryOn={false}
                    showMember={false}
                    showTag={false}
                    showOriginalPrice={false}
                    showName={false}
                    nameSuffix={indexData.name_suffix}
                    imageClassName="!aspect-[416/500] !rounded-[0px]"
                    imageHoverRule={[]}
                    namePriceInline={false}
                    handelClick={(cardIndex, product) => {
                      handleSDHomeMktClick(product as ListProduct, cardIndex, 0);
                    }}
                    onInView={(cardIndex, product) => {
                      handleExposeFunc(product as ListProduct, cardIndex, 0);
                    }}
                    onValidExposure={(cardIndex, product) => {
                      handleValidExposure(product as ListProduct, cardIndex, 0);
                    }}
                  />
                </SwiperSlide>
              ))
            : Array.from({ length: isPC ? 4 : 2 }).map((_, index) => (
                <SwiperSlide
                  key={`skeleton-${index}`}
                  className={`
                  swiper-item relative
                  ${isPC ? '!w-[23%] mr-[16px] last:!mr-0 !min-w-[328px] 2xl:!min-w-[auto]' : ''}
                `}
                >
                  <div className="flex items-center justify-center w-[150px] lg:w-[328px] aspect-[150/324] lg:aspect-[328/503] ">
                    <SkeletonProductCard />
                  </div>
                </SwiperSlide>
              ))}
        </Swiper>

        {/* PC端导航按钮 - 使用CSS文件中的样式类 */}
        {isPC && (
          <>
            <div
              className={`
                ${uniqueId}-next product-swiper-navigation absolute z-10 flex items-center justify-center
                w-[75px] lg:w-[100px] h-[53px] lg:h-[70px]
                top-[137px] lg:top-[185px] right-[48px] lg:right-[105px]
                rounded-[35px] border-2 border-white text-white cursor-pointer
              `}
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
              }}
            >
              <ToRightArrow className="text-[30px] lg:text-[40px]" />
            </div>
            <div
              className={`
                ${uniqueId}-prev product-swiper-navigation absolute z-10 flex items-center justify-center
                w-[75px] lg:w-[100px] h-[53px] lg:h-[70px]
                top-[137px] lg:top-[185px] left-[48px] lg:left-[105px]
                rounded-[35px] border-2 border-white text-white cursor-pointer
              `}
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
              }}
            >
              <ToRightArrow className="text-[30px] lg:text-[40px] rotate-180" />
            </div>
          </>
        )}
      </div>

      {/* 移动端View More按钮 */}
      {!isPC && indexData.btn_display === 1 && (
        <div className="mt-[30px] flex justify-center">
          <Link
            href={`/goods-list/${productGroupPath}`}
            className="view-more min-w-[190px] h-[40px] leading-[40px] text-center rounded-[20px] text-[14px] font-bb relative transition-all duration-200 hover:opacity-90"
            style={{
              backgroundColor: indexData.btn_bg_color || '#333',
              color: indexData.btn_word_color || '#fff',
            }}
            onClick={() => {
              setPageFlagsInfo();
              setSession('zl_goodsListEnterPageScene', '首页坑位');
            }}
          >
            {indexData.btn_text || 'View More'}
          </Link>
        </div>
      )}
    </div>
  );
}
